import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Dashboard ErrorBoundary]', error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <div className="max-w-md space-y-2">
            <h2 className="text-lg font-semibold">{i18n.t('errors.boundaryTitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || i18n.t('errors.boundaryFallback')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
          >
            {i18n.t('errors.reload')}
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
